import React from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import { NINKYCSchema } from "@njoy/common";
import { ToastContainer } from "react-toastify";
import { useVerifyNIN, ViewMe } from "@njoy/controller";
import "react-toastify/dist/ReactToastify.css";

import ToastMessage from "../../hooks/useToast";
import { extractError } from "../../utils/extractError";
import { openSupportEscalationMail } from "../../utils/supportEscalation";
import { NewModal } from "../uiMerge/newModal";
import Button from "../uiMerge/newButton";
import { InputField } from "../../modules/shared/InputField";

import * as ROUTES from "../../constants/routes";

type VerifyNinModalProps = {
    isOpen: boolean;
    onClose: () => void;
}

export const VerifyNinModal: React.FC<VerifyNinModalProps> = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const { verifyNIN } = useVerifyNIN();
    const [isLoading, setIsLoading] = React.useState<boolean>(false)
    const [supportIssue, setSupportIssue] = React.useState<{
        message: string;
        errorCode?: string;
        supportRef?: string;
    } | null>(null);

    const handleSubmit = async (
        values: { nin: string; firstname: string; lastname: string },
        { setSubmitting, setErrors, resetForm }: any
    ) => {
        setSubmitting(true);
        setIsLoading(true);
        try {
            const result = await verifyNIN({ nin: values.nin, firstname: values.firstname, lastname: values.lastname });
            setSubmitting(false);

            if (!result.errors || Object.keys(result.errors).length === 0) {
                setSupportIssue(null);
                // Show warning if partial match, else success
                if (result.message && result.message.toLowerCase().includes('partial match')) {
                    ToastMessage("warning", result.message);
                } else {
                    ToastMessage("success", result.message || "NIN verified successfully!");
                }
                resetForm();
                
                // Wait for toast to show before redirecting
                setTimeout(() => {
                    onClose();
                    navigate(ROUTES.CONNECT_ID);
                    window.location.reload();
                }, 1800); // 1.8s for toast to be visible
                return;
            }

            const errorSummary = extractError(result.errors);
            const supportMeta = (result as {
                supportMeta?: {
                    code?: string;
                    supportRef?: string;
                };
            }).supportMeta;

            setSupportIssue({
                message: errorSummary,
                errorCode: supportMeta?.code,
                supportRef: supportMeta?.supportRef,
            });
            ToastMessage("error", errorSummary);
            setErrors(result.errors);
            setIsLoading(false);
        } catch (err) {
            setSubmitting(false);
            setIsLoading(false);
            setSupportIssue({ message: "Something went wrong. Please try again." });
            ToastMessage("error", "Something went wrong. Please try again.");
        }
    };

    return (
        <>
            <ToastContainer />
            
            <NewModal
            isOpen={isOpen}
            onClose={onClose}
            position="end"
            colorClass="bg-black"
            animationClass="grow-in"
            showCloseButton={false}
            canClickOutsideToClose={true}>
                <ViewMe>
                    {({match, loading}) => {
                        if (loading) {
                            return <p className="text-[#9fafb5] flex justify-center items-center h-screen">Loading...</p>;
                        }

                        return(
                            <Formik
                                initialValues={{
                                    nin: "",
                                    firstname: match?.customer?.firstname || "",
                                    lastname: match?.customer?.lastname || ""
                                }}
                                validationSchema={NINKYCSchema}
                                onSubmit={handleSubmit}
                            >
                                <Form>
                                    <div className="flex flex-col gap-4 px-6">
                                        <div className="flex flex-col items-center gap-3">
                                            <div onClick={onClose} className="bg-white w-[55px] h-[4px] mt-5 rounded-2xl flex items-center " />
                                            <div className="flex flex-col gap-5 items-center mt-6">
                                                <p className="text-white text-[24px] font-extrabold">
                                                    Verify Your NIN
                                                </p>
                                                <p className="text-white text-[16px] text-center">
                                                    All data provided should be as it appears on your government-issued ID.
                                                </p>
                                            </div>
                                        </div>

                                        {/* Fields */}
                                        <div className="flex flex-col gap-2">
                                            <div className="flex flex-col ">
                                                <label className="text-[#CECECF]" htmlFor="nin">
                                                    National Identification Number (NIN)
                                                </label>
                                                <Field
                                                name="nin"
                                                placeholder="Enter Your NIN"
                                                className="rounded-lg bg-[#2E2E2E] p-3 text-white"
                                                component={InputField} />
                                            </div>

                                            <div className="flex flex-col ">
                                                <label className="text-[#CECECF]" htmlFor="bvn">
                                                    First Name
                                                </label>
                                                <Field
                                                name="firstname"
                                                placeholder="First Name"
                                                className="rounded-lg bg-[#2E2E2E] p-3 text-white"
                                                component={InputField} />
                                            </div>

                                            <div className="flex flex-col">
                                                <label className="text-[#CECECF]" htmlFor="dob">
                                                    Last Name
                                                </label>
                                                <Field
                                                name="lastname"
                                                placeholder="Last Name"
                                                className="rounded-lg bg-[#2E2E2E] p-3 text-white"
                                                component={InputField} />
                                            </div>

                                            <p className="text-[14px] text-[#8E8E8E] text-center  mb-9">
                                                You can check your NIN by dialing{" "}
                                                <span className="font-extrabold text-[#8E8E8E] text-[15px]">
                                                    *346#
                                                </span>{" "}
                                                using the phone number you used to register your NIN.
                                            </p>
                                        </div>

                                        {/* Buttons */}
                                        <div className="flex gap-4 mt-3 mb-3">
                                            <Button
                                            variant="outline"
                                            onClick={() => {
                                                onClose();
                                                setTimeout(() => navigate(ROUTES.HOME), 100);
                                            }}
                                            fullWidth>
                                                Go Back
                                            </Button>

                                            <Button
                                            loading={isLoading}
                                            type="submit"
                                            fullWidth>
                                                Continue
                                            </Button>
                                        </div>
                                            {supportIssue && (
                                                <div className="mb-4">
                                                    <Button
                                                        variant="outline"
                                                        onClick={() =>
                                                            openSupportEscalationMail({
                                                                actionName: "Verify NIN",
                                                                errorMessage: supportIssue.message,
                                                                errorCode: supportIssue.errorCode,
                                                                supportRef: supportIssue.supportRef,
                                                                operationName: "VerifyNinMutation",
                                                                extra: {
                                                                    ninLength: 11,
                                                                    firstnameProvided: Boolean(match?.customer?.firstname),
                                                                    lastnameProvided: Boolean(match?.customer?.lastname),
                                                                },
                                                            })
                                                        }
                                                        fullWidth
                                                    >
                                                        Need help? Contact Support
                                                    </Button>
                                                </div>
                                            )}
                                    </div>
                                </Form>
                            </Formik>
                        );
                    }}
                </ViewMe>
            </NewModal>
        </>
    );
};

